import React from 'react'
import { motion } from 'framer-motion'
import BaseButton from '../../components/common/BaseButton'
import { useNavigate } from 'react-router-dom'
import styles from '../../styles/user/landing.module.scss'

const Landing = () => {
  const navigate = useNavigate()

  const handleNavigateToSignup = () => {
    navigate('/signup')
  }

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  }

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  }

  return (
    <motion.div
      className={styles.landingContainer}
      initial="hidden"
      animate="visible"
    >
      {/* 배경 이미지들 */}
      <motion.img
        src="/assets/landing-background1.png"
        alt="background"
        className={styles.logoTopImage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <motion.img
        src="/assets/landing-background2.png"
        alt="background"
        className={styles.logoMiddleFirstImage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
      />
      <motion.img
        src="/assets/landing-background2.png"
        alt="background"
        className={styles.logoMiddleSecondImage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
      />
      <motion.img
        src="/assets/landing-background2.png"
        alt="background"
        className={styles.logoMiddleThirdImage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.6 }}
      />
      <motion.img
        src="/assets/landing-background3.png"
        alt="background"
        className={styles.logoBottomImage}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
      />

      <motion.div
        className={styles.contentBoxes}
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {/* Intro 섹션 */}
        <motion.section className={styles.landingBox} variants={fadeInUp}>
          <motion.p className={styles.lgText} variants={fadeInUp}>
            <span className={styles.blueText}>Structured math </span>education
            for <br />
            <span className={styles.yellowText}>elementary students</span>
          </motion.p>
          <motion.p className={styles.smText} variants={fadeInUp}>
            Self-paced online learning that builds mastery <br /> from concepts
            to application through video <br />
            lessons and mistake-based review
          </motion.p>
          <motion.img
            src="/assets/landing-image1.png"
            alt="Landing"
            className={styles.landingFirstImage}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          />
          <motion.img
            src="/assets/landing-image2.png"
            alt="Landing"
            className={styles.landingSecondImage}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          />
          <motion.img
            src="/assets/landing-image3.png"
            alt="Landing"
            className={styles.landingThirdImage}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            viewport={{ once: true }}
          />
        </motion.section>

        {/* Curriculum Section */}
        <motion.section
          className={styles.curriculumSection}
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.span className={styles.pinkText} variants={fadeIn}>
            Students
          </motion.span>

          <motion.p className={styles.smTextFirst} variants={fadeInUp}>
            Elementary students, students that need self-paced or advanced
            <br />
            learning, homeschooling families or education centers
          </motion.p>

          <motion.div
            className={styles.highlightWrapper}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.div className={styles.highlightText}>Curriculum</motion.div>
          </motion.div>

          <motion.p className={styles.smTextSecond} variants={fadeInUp}>
            Students can learn at their pace through 12 total levels (level
            1-12),
          </motion.p>
          <motion.p className={styles.smTextThird} variants={fadeInUp}>
            with each level structured into{' '}
            <span className={styles.bold}>
              Units, Chapters, Lesson, <br />
              and Sub-lessons —
              <span className={styles.highlightOrangeWrapper}>
                <span className={styles.highlightOrangeText}>
                  featuring over 1000 sub-lessons in total
                </span>
              </span>
            </span>
          </motion.p>

          <motion.img
            src="/assets/landing-image4.png"
            alt="Landing"
            className={styles.landingFourthImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          />
          <motion.img
            src="/assets/landing-image5.png"
            alt="Landing"
            className={styles.landingFifthImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          />
          <motion.img
            src="/assets/landing-image6.png"
            alt="Landing"
            className={styles.landingSixthImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          />
        </motion.section>

        {/* Why Solution 섹션 */}
        <motion.section
          className={styles.boxContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div className={styles.highlightLgWrapper} variants={fadeInUp}>
            <motion.p className={styles.highlightLgText}>
              Why Solution M?
            </motion.p>
          </motion.div>

          <motion.div className={styles.boxGroup} variants={staggerContainer}>
            <motion.div className={styles.boxRow} variants={fadeInUp}>
              <motion.div className={styles.boxColumn} variants={fadeIn}>
                <div className={styles.boxLeft}>
                  <img
                    src="/assets/landing-icon1.svg"
                    alt="Landing"
                    className={styles.landingIcon}
                  />
                  <motion.div className={styles.boxTitle}>
                    Concepts
                    <br />
                    Problems
                  </motion.div>
                </div>
                <div className={styles.boxRight}>
                  <p className={styles.boxSubtitle}>
                    Optimal review-repeat <br /> learning flow
                  </p>
                  <p className={styles.boxContent}>
                    Fully equipped with the fundamental elements of elementary
                    mathematics: mastery of concepts and accumulation of
                    problem-solving experience
                  </p>
                </div>
              </motion.div>

              <motion.div className={styles.boxColumn} variants={fadeIn}>
                <div className={styles.boxLeft}>
                  <img
                    src="/assets/landing-icon2.svg"
                    alt="Landing"
                    className={styles.landingIcon}
                  />
                  <motion.div className={styles.boxTitle}>Solutions</motion.div>
                </div>
                <div className={styles.boxRight}>
                  <p className={styles.boxSubtitle}>
                    Detailed solution explanations for every problem
                  </p>
                  <p className={styles.boxContent}>
                    Independent study without the need for teachers or guardians
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div className={styles.boxRow} variants={fadeInUp}>
              <motion.div className={styles.boxColumn} variants={fadeIn}>
                <div className={styles.boxLeft}>
                  <img
                    src="/assets/landing-icon3.svg"
                    alt="Landing"
                    className={styles.landingIcon}
                  />
                  <motion.div className={styles.boxTitle}>Learning</motion.div>
                </div>
                <div className={styles.boxRight}>
                  <p className={styles.boxSubtitle}>
                    Repetitive learning through mistake review
                  </p>
                  <p className={styles.boxContent}>
                    Maximize understanding and efficiency by reattempting
                    incorrect problems
                  </p>
                </div>
              </motion.div>

              <motion.div className={styles.boxColumn} variants={fadeIn}>
                <div className={styles.boxLeft}>
                  <img
                    src="/assets/landing-icon4.svg"
                    alt="Landing"
                    className={styles.landingIcon}
                  />
                  <motion.div className={styles.boxTitle}>Structure</motion.div>
                </div>
                <div className={styles.boxRight}>
                  <p className={styles.boxSubtitle}>
                    Designed with a sole focus on mathematics
                  </p>
                  <p className={styles.boxContent}>
                    The program is carefully designed to reflect the progression
                    between units, the connections between concepts, and the
                    patterns behind student mistakes—enabling organized learning
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Image 섹션 */}
        <motion.section
          className={styles.imageSection}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.img
            src="/assets/landing-image7.png"
            alt="Landing"
            className={styles.landingSevenImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          />
          <motion.p className={styles.imageSectionTitle} variants={fadeInUp}>
            Students learn independently, <br />
            <div className={styles.highlightYellowWrapper}>
              <motion.span className={styles.highlightYellowText}>
                while guardians stay involved
              </motion.span>
            </div>
          </motion.p>

          <motion.p className={styles.smTextFourth} variants={fadeInUp}>
            Guardian access through a separate account to view <br />
            the student's learning progress in real-time
          </motion.p>
          <motion.img
            src="/assets/dashboard.png"
            alt="dashboard"
            className={styles.dashboardImage}
            variants={slideInRight}
          />
          <motion.img
            src="/assets/landing-image8.png"
            alt="Landing"
            className={styles.landingEightImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          />
        </motion.section>

        {/* Technical features 섹션 */}
        <motion.section
          className={styles.technicalSection}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.img
            src="/assets/landing-image9.png"
            alt="Landing"
            className={styles.landingNineImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          />
          <motion.img
            src="/assets/landing-image10.png"
            alt="Landing"
            className={styles.landingTenImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          />
          <motion.div className={styles.highlightSmWrapper} variants={fadeInUp}>
            <motion.p className={styles.highlightSmText}>
              Technical features
            </motion.p>
          </motion.div>

          <motion.div className={styles.boxRow} variants={staggerContainer}>
            <motion.div className={styles.technicalBox} variants={fadeInUp}>
              <img
                src="/assets/technical-image1.png"
                alt="dashboard"
                className={styles.technicalImage}
              />
              <motion.div className={styles.technicalTitle}>
                Web-based
              </motion.div>

              <p className={styles.technicalContent}>
                Start learning with <br />
                no separate installation
              </p>
            </motion.div>

            <motion.div className={styles.technicalBox} variants={fadeInUp}>
              <img
                src="/assets/technical-image2.png"
                alt="dashboard"
                className={styles.technicalImage}
              />

              <motion.div className={styles.technicalTitle}>
                Responsive design
              </motion.div>
              <p className={styles.technicalContent}>
                Smooth learning on web, tablet, <br /> computer etc
              </p>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <p className={styles.technicalBottomText}>
              A separate admin dashboard is provided for group clients
            </p>
          </motion.div>
        </motion.section>

        {/* We built it 섹션 */}
        <motion.section
          className={styles.builtSection}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div className={styles.highlightMdWrapper} variants={fadeInUp}>
            <motion.p className={styles.highlightMdText}>We built it</motion.p>
          </motion.div>
          <motion.img
            src="/assets/landing-image11.png"
            alt="Landing"
            className={styles.landingElevenImage}
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          />

          <motion.p className={styles.builtTitle} variants={fadeInUp}>
            Our self-directed learning system is developed by math education
            experts
          </motion.p>

          <motion.p className={styles.builtSubtitle} variants={fadeInUp}>
            The curriculum was analyzed and designed by education planners
            <br /> with deep knowledge of elementary math classrooms and
            national curriculums
          </motion.p>

          <motion.img
            src="/assets/landing-image12.png"
            alt=""
            className={styles.builtImage}
            variants={fadeIn}
          />

          <motion.p className={styles.builtTitle} variants={fadeInUp}>
            design based on the most effective and continuous learning flow
          </motion.p>

          <motion.img
            src="/assets/landing-image13.png"
            alt=""
            className={styles.builtSecondImage}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          />
        </motion.section>

        <motion.div
          className={styles.contentWrapper}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.p className={styles.builtTitle}>
            Every element—including the level structure, review cycle, mistake
            selection method, and solutions —was designed with a clear focus on
            helping students solidify math concepts and strengthen their
            problem-solving skills
          </motion.p>
        </motion.div>

        {/* Question Section */}
        <motion.section
          className={styles.questionSection}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <motion.h2 className={styles.questionTitle} variants={fadeIn}>
            Have a question?
          </motion.h2>
          <motion.p className={styles.questionContent} variants={fadeIn}>
            Whether you're an individual or representing a group, <br /> feel
            free to email us at mhedusolution@mhesol.com!
          </motion.p>
        </motion.section>
      </motion.div>

      <motion.div
        className={styles.landingButtonWrapper}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <BaseButton
          onClick={handleNavigateToSignup}
          className={styles.landingButton}
        >
          Get Started
        </BaseButton>
      </motion.div>
    </motion.div>
  )
}

export default Landing
